import React, { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { fmtUsd } from "../../data/casesData";
import { Users, Copy, Check, Loader2, Edit3 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const t = localStorage.getItem("uc_token");
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

export default function AffiliatesModal({ open, onOpenChange }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    if (!user) { setData(null); return; }
    try { const r = await axios.get(`${API}/affiliate`, authCfg()); setData(r.data); setNewCode(r.data.code || ""); } catch { /* ignore */ }
  };
  useEffect(() => { if (open) { setErr(""); load(); } /* eslint-disable-next-line */ }, [open, user]);

  const link = data ? `${window.location.origin}/?ref=${data.code}` : "";

  const copy = () => { navigator.clipboard?.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1800); };

  const saveCode = async () => {
    setSaving(true); setErr("");
    try {
      const r = await axios.post(`${API}/affiliate/code`, { code: newCode }, authCfg());
      setData((d) => ({ ...d, code: r.data.code }));
    } catch (e) { setErr(e.response?.data?.detail || "Could not update code"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="affiliates-modal" className="max-w-md rounded-2xl border-[var(--uc-border)] bg-[#15112A] text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl font-extrabold">
            <Users className="h-5 w-5 text-[var(--uc-primary)]" /> Affiliates
          </DialogTitle>
        </DialogHeader>

        {!user ? (
          <div className="py-6 text-center">
            <p className="text-sm text-[var(--uc-muted)]">Earn 5% of every deposit your friends make, as promo credits.</p>
            <button onClick={() => { onOpenChange(false); window.dispatchEvent(new Event("uc:open-auth")); }}
              className="mt-4 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] px-5 py-2.5 text-sm font-bold text-white">Sign in to get your link</button>
          </div>
        ) : !data ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[var(--uc-muted)]" /></div>
        ) : (
          <>
            <p className="text-sm text-[var(--uc-muted)]">
              Earn <span className="font-bold text-white">5%</span> of every deposit your referrals make as promo credits (playable, then withdrawable once wagered through).
            </p>

            <div className="grid grid-cols-3 gap-2">
              {[["Referrals", data.referrals], ["Earned", fmtUsd(data.promo_earned)], ["Rate", "5%"]].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-[var(--uc-border)] bg-[var(--uc-card)] p-3 text-center">
                  <div className="font-heading text-lg font-extrabold text-white">{v}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[var(--uc-muted)]">{k}</div>
                </div>
              ))}
            </div>

            {data.promo_credits > 0 && (
              <div className="rounded-xl border border-[var(--uc-border)] bg-[var(--uc-card)] p-3 text-[12px]">
                <div className="flex justify-between"><span className="text-[var(--uc-muted)]">Promo balance</span><span className="font-bold text-[#C4B5FD]">{fmtUsd(data.promo_credits)}</span></div>
                <div className="flex justify-between"><span className="text-[var(--uc-muted)]">Wager left to unlock</span><span className="font-bold text-white">{fmtUsd(data.wager_required)}</span></div>
              </div>
            )}

            <div>
              <label className="text-xs font-semibold text-[var(--uc-muted)]">Your custom code</label>
              <div className="mt-1 flex items-center gap-2">
                <div className="relative flex-1">
                  <Edit3 className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--uc-muted)]" />
                  <input value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())} data-testid="affiliate-code-input" maxLength={16}
                    className="w-full rounded-xl border border-[var(--uc-border)] bg-[var(--uc-bubble)] py-2.5 pl-9 pr-3 text-sm font-bold tracking-wide text-white focus:border-[var(--uc-border-active)] focus:outline-none" />
                </div>
                <button onClick={saveCode} disabled={saving || !newCode || newCode === data.code} data-testid="affiliate-code-save"
                  className="flex h-10 items-center rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] px-3 text-sm font-bold text-white disabled:opacity-40">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                </button>
              </div>
              {err && <div data-testid="affiliate-code-error" className="mt-1 text-[11px] text-[#FDA4CE]">{err}</div>}
            </div>

            <div>
              <label className="text-xs font-semibold text-[var(--uc-muted)]">Your referral link</label>
              <div className="mt-1 flex items-center gap-2">
                <input readOnly value={link} data-testid="affiliate-link"
                  className="flex-1 truncate rounded-xl border border-[var(--uc-border)] bg-[var(--uc-bubble)] px-3 py-2.5 text-sm text-white" />
                <button onClick={copy} data-testid="affiliate-copy-btn"
                  className="flex h-10 items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-3 text-sm font-bold text-white hover:bg-white/10">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
